import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Grid, Button, CircularProgress } from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { toast } from "react-toastify";
import { PaymentService } from "../../../services/PaymentService";
import { MOP_INFO } from "../../../constants/paymentMaps";
import { openPaymentPopup } from "../../../utils/openPaymentPopup";

const NetBankingSection = ({ mops, txn, onPaymentInitiated }) => {
  const navigate = useNavigate();
  const [selectedBank, setSelectedBank] = useState("");
  const [submitting, setSubmitting]     = useState(false);

  const handlePaymentFailure = (message) => {
    navigate("/response-page", {
      replace: true,
      state: {
        response: {
          status:          "FAILED",
          responseMessage: message,
          txnId:           txn.transactionId,
          orderId:         txn.orderId,
          amount:          txn.amount,
        },
      },
    });
  };

  const handlePay = async () => {
    if (!selectedBank) { toast.warning("Please select a bank"); return; }
    setSubmitting(true);
    try {
      const res = await PaymentService.submitPayReq({
        orderId:         txn.transactionId,
        paymentType:     "NB",
        mopType:         selectedBank,
        vpa: "", cardType: "", cardNumber: "",
        cardExpiryMonth: "", cardExpiryYear: "",
        cardSecurity: "", cardHolderName: "",
      });
      if (res.statusCode === 200) {
        const link = res.paymentLink || res.intentLink;
        const popup = link ? openPaymentPopup(link) : null;
        onPaymentInitiated("netbanking", popup);
      } else {
        toast.error(res.message || "Payment request failed");
        handlePaymentFailure(res.message || "Payment request failed");
      }
    } catch (err) {
      console.error("NetBanking payment error:", err);
      const msg = "An error occurred during payment";
      toast.error(msg);
      handlePaymentFailure(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLabel = MOP_INFO[selectedBank]?.label || selectedBank;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} color="#1f2937" mb={2}>
        Select Your Bank
      </Typography>

      <Grid container spacing={1.5}>
        {mops.map((bank) => {
          const info = MOP_INFO[bank] || { label: bank, logo: null };
          const isSelected = selectedBank === bank;
          return (
            <Grid item xs={12} sm={6} key={bank}>
              <Button
                fullWidth
                variant={isSelected ? "contained" : "outlined"}
                onClick={() => setSelectedBank(bank)}
                startIcon={
                  info.logo
                    ? <Box component="img" src={info.logo} alt={info.label} sx={{ height: 22, objectFit: "contain" }} />
                    : <AccountBalanceIcon />
                }
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  borderRadius: "10px",
                  py: 1.5, px: 2,
                  fontWeight: 500,
                  ...(isSelected
                    ? {
                        background: "#15b86d",
                        borderColor: "#15b86d",
                        color: "white",
                        boxShadow: "0 4px 14px rgba(21,184,109,0.25)",
                        "&:hover": { background: "#13a360" },
                      }
                    : {
                        borderColor: "#e5e7eb",
                        color: "#374151",
                        "&:hover": { borderColor: "#15b86d", color: "#15b86d", bgcolor: "#f0fdf4" },
                      }
                  ),
                }}
              >
                {info.label}
              </Button>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={3}>
        <Button
          fullWidth
          variant="contained"
          onClick={handlePay}
          disabled={!selectedBank || submitting}
          sx={{
            py: 1.5, fontWeight: 600, borderRadius: "10px",
            textTransform: "none", fontSize: "1rem",
            background: "#15b86d",
            "&:hover": { background: "#13a360" },
            "&:disabled": { background: "#e5e7eb", color: "#9ca3af" },
          }}
        >
          {submitting
            ? <CircularProgress size={22} sx={{ color: "white" }} />
            : selectedBank
              ? `Pay ₹${parseFloat(txn.amount || 0).toFixed(2)} via ${selectedLabel}`
              : `Pay ₹${parseFloat(txn.amount || 0).toFixed(2)}`}
        </Button>
      </Box>
    </Box>
  );
};

export default NetBankingSection;
